import { useEffect, useState } from "react";
import { Building2, Users, Sun, Moon } from "lucide-react";
import KPICard from "../../components/charts/KPICard";
import { companyAPI } from "../../services/api";
import { useTheme } from "../../context/ThemeContext";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function SuperDashboard() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    companyAPI
      .stats()
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.message));
  }, []);

  if (err)
    return (
      <div style={{ padding: 28, color: "var(--danger)" }}>Error: {err}</div>
    );
  if (!data)
    return <div style={{ padding: 28, color: "var(--text2)" }}>Loading...</div>;

  const { totals = {}, weekly = [], companyOverview = [] } = data;
  const weeklyChart = weekly.map((w) => ({
    day: DAY_LABELS[new Date(w.day).getDay()],
    checks: w.checks,
  }));

  return (
    <div style={{ padding: 28, position: "relative" }}>
      {/* Theme toggle — fixed top-right corner of Dashboard (same pattern as Company Dashboard) */}
      <div
        onClick={toggleTheme}
        role="switch"
        aria-checked={theme === "light"}
        title={
          theme === "dark" ? "Switch to Light Mode" : "Switch to Dark Mode"
        }
        style={{
          position: "absolute",
          top: 28,
          right: 28,
          zIndex: 5,
          width: 52,
          height: 26,
          borderRadius: 999,
          background: "var(--surface2)",
          border: "1px solid var(--border2)",
          cursor: "pointer",
        }}
      >
        <Sun
          size={12}
          style={{
            position: "absolute",
            left: 6,
            top: "50%",
            transform: "translateY(-50%)",
            color: theme === "light" ? "var(--warn)" : "var(--text3)",
          }}
        />
        <Moon
          size={12}
          style={{
            position: "absolute",
            right: 6,
            top: "50%",
            transform: "translateY(-50%)",
            color: theme === "dark" ? "var(--accent)" : "var(--text3)",
          }}
        />
        <div
          style={{
            width: 20,
            height: 20,
            borderRadius: "50%",
            background: theme === "dark" ? "var(--accent)" : "var(--warn)",
            position: "absolute",
            top: 2,
            left: theme === "dark" ? 28 : 2,
            transition: "left 0.2s ease, background 0.2s ease",
            boxShadow: "0 1px 3px rgba(0,0,0,0.4)",
          }}
        />
      </div>

      <div
        className="page-header"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 24,
        }}
      >
        <div>
          <h1
            style={{ fontSize: 20, fontWeight: 700, letterSpacing: "-0.02em" }}
          >
            Super Admin Dashboard
          </h1>
          <p style={{ color: "var(--text2)", fontSize: 13, marginTop: 2 }}>
            Platform-wide overview — all companies & clients
          </p>
        </div>
      </div>

      {/* Only 2 KPIs now — full-width row, matches the chart/table row below it */}
      <div
        className="dashboard-cards-grid"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(2, 1fr)",
          gap: 16,
          marginBottom: 24,
        }}
      >
        <KPICard
          label="Total Companies"
          value={totals.total_companies}
          icon={Building2}
          color="var(--accent2)"
        />
        <KPICard
          label="Total Clients"
          value={totals.total_clients}
          icon={Users}
          color="var(--accent)"
        />
      </div>

      <div
        className="dashboard-charts-grid"
        style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr", gap: 16 }}
      >
        <div className="card">
          <h3
            style={{
              fontSize: 13,
              fontWeight: 600,
              marginBottom: 16,
              color: "var(--text2)",
            }}
          >
            Weekly Weight Checks
          </h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart
              data={weeklyChart}
              margin={{ top: 0, right: 0, bottom: 0, left: -20 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis
                dataKey="day"
                tick={{ fill: "var(--text3)", fontSize: 11 }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fill: "var(--text3)", fontSize: 11 }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                contentStyle={{
                  background: "var(--surface)",
                  border: "1px solid var(--border2)",
                  borderRadius: 8,
                }}
                labelStyle={{ color: "var(--text2)" }}
                itemStyle={{ color: "var(--accent)" }}
              />
              <Bar
                dataKey="checks"
                fill="var(--accent)"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="card" style={{ padding: 0, overflow: "hidden" }}>
          <div
            style={{
              padding: "16px 20px",
              borderBottom: "1px solid var(--border)",
            }}
          >
            <h3 style={{ fontSize: 13, fontWeight: 600 }}>Company Overview</h3>
          </div>
          <table>
            <thead>
              <tr>
                <th>Company</th>
                <th>Clients</th>
                <th>Total Checks</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {companyOverview.map((c, i) => (
                <tr key={i}>
                  <td style={{ fontWeight: 500 }}>{c.name}</td>
                  <td className="mono">{c.client_names || "—"}</td>
                  <td className="mono">
                    {Number(c.total_checks).toLocaleString()}
                  </td>
                  {/* <td>{c.alerts > 0
                    // ? <span className="badge badge-warn">{c.alerts}</span>
                    : <span className="badge badge-success">0</span>}
                  </td> */}
                  <td>
                    <span
                      className={`badge ${c.status === "Active" ? "badge-success" : "badge-info"}`}
                    >
                      {c.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
